import React from "react";
import { Link } from "react-router-dom";
import {
  Heart, Briefcase, Building2, Scale, Shield, FileText, Gavel,
  Users, Home as HomeIcon, Lightbulb, ArrowLeft, ArrowRight,
  MessageCircle,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import { SERVICES } from "../data/services";
import { buildWhatsAppLink, getPageWaLink } from "../data/firm";

const ICONS = { Heart, Briefcase, Building2, Scale, Shield, FileText, Gavel, Users, Home: HomeIcon, Lightbulb };

export default function Services() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  return (
    <div data-testid="services-page">
      <SEO
        path="/services"
        titleAr="خدماتنا القانونية | مجموعة الدخيل القانونية الكويت"
        titleEn="Legal Services | Aldakheel Legal Group Kuwait"
        descriptionAr="عشرة تخصصات قانونية: أحوال شخصية، قضايا تجارية، عمالية، مدنية، جزائية، شركات، عقود، تحكيم، عقارات، ملكية فكرية."
        descriptionEn="Ten legal practices: family, commercial, labor, civil, criminal, corporate, contracts, arbitration, real estate, intellectual property."
        jsonLd={[orgJsonLd(), breadcrumbJsonLd([
          { name: isAr ? "الرئيسية" : "Home", path: "/" },
          { name: isAr ? "خدماتنا" : "Services", path: "/services" },
        ])]}
      />

      <section className="section-y" style={{ background: "var(--paper)" }}>
        <div className="container-luxe text-center max-w-3xl">
          <p className="eyebrow">{isAr ? "خدماتنا" : "Services"}</p>
          <h1 className={`mt-4 ${isAr ? "font-arabic" : "font-display"} gold-rule center`}
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)", lineHeight: 1.2 }}>
            {isAr ? "عشرة تخصصات قانونية تحت سقف واحد" : "Ten legal disciplines under one roof"}
          </h1>
          <p className={`mt-8 ${isAr ? "font-arabic" : ""} text-lg leading-relaxed`} style={{ color: "var(--ink-soft)" }}>
            {isAr
              ? "نقدّم خدمات قانونية شاملة في مجالات: الأحوال الشخصية، والعمل، والقضايا التجارية والمدنية والجزائية، والشركات، والعقود، والتحكيم، والعقارات، والملكية الفكرية."
              : "Comprehensive legal services across family, labor, commercial, civil, criminal, corporate, contracts, arbitration, real estate, and intellectual property."}
          </p>
        </div>
      </section>

      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Scale;
              return (
                <div key={s.slug} className="luxe-card p-7 flex flex-col">
                  <div className="w-12 h-12 rounded-sm flex items-center justify-center mb-5"
                       style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={`${isAr ? "font-arabic" : "font-display"} text-xl`}
                      style={{ fontWeight: isAr ? 700 : 500, color: "var(--heading)" }}>
                    {isAr ? s.titleAr : s.titleEn}
                  </h3>
                  <p className={`mt-3 ${isAr ? "font-arabic" : ""} text-sm leading-relaxed flex-1`}
                     style={{ color: "var(--ink-soft)" }}>
                    {isAr ? s.teaserAr : s.teaserEn}
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row gap-2">
                    <Link to={`/services/${s.slug}`} className="flex-1" data-testid={`services-${s.slug}-detail`}>
                      <Button variant="outline" className="rounded-sm w-full h-10 border-[var(--gold)] text-[var(--gold-deep)] hover:bg-[var(--gold)] hover:text-[var(--navy-deep)] text-sm">
                        {isAr ? "التفاصيل" : "Details"}
                        {isAr ? <ArrowLeft className="w-3.5 h-3.5 ms-1" /> : <ArrowRight className="w-3.5 h-3.5 ms-1" />}
                      </Button>
                    </Link>
                    <a href={getPageWaLink(s.slug, lang)} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid={`services-${s.slug}-book`}>
                      <Button className="btn-gold rounded-sm w-full h-10 text-sm">
                        {isAr ? "احجز" : "Book"}
                      </Button>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-y" style={{ background: "var(--beige)" }}>
        <div className="container-luxe text-center max-w-2xl">
          <h2 className={`${isAr ? "font-arabic" : "font-display"}`}
              style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
            {isAr ? "غير متأكد من المجال الذي يناسبك؟" : "Not sure which practice fits?"}
          </h2>
          <p className="mt-4" style={{ color: "var(--ink-soft)" }}>
            {isAr ? "اسأل سامي، أو راسلنا على واتساب." : "Ask Sami quickly, or message us on WhatsApp."}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/ai-legal-assistant">
              <Button className="btn-navy rounded-sm px-7 h-12">{isAr ? "اسأل الذكاء القانوني" : "Ask AI"}</Button>
            </Link>
            <a href={getPageWaLink("services", lang)} target="_blank" rel="noopener noreferrer" data-testid="services-cta-whatsapp">
              <Button className="btn-whatsapp rounded-sm px-7 h-12">
                <MessageCircle className="w-4 h-4 me-2" />
                {isAr ? "تواصل واتساب" : "WhatsApp Us"}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
